package wat.ui

import kotlinx.browser.document
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService
import kotlin.js.Date

object TrainTab {

    private var selectedDayFilter: String? = null
    private var selectedSportForAdd: Sport? = null
    private var lastAddedSportKey: String? = null
    private val expandedGroups = mutableSetOf<String>()

    fun resetAddForm() { selectedSportForAdd = null }

    // Section title style constant
    private val SECTION_TITLE get() = "font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT_40}"

    fun render(container: HTMLDivElement, userId: String, showForm: Boolean, onSave: () -> Unit) {
        // When add form is open, show only the form
        if (showForm) {
            container.appendChild(renderAddActivity(userId, onSave))
            return
        }

        // Deduplicate: keep only the latest entry per day (first in desc sort)
        // Old INSERT bug created duplicate rows per date, inflating totals
        val entries = DataService.entries.sortedByDescending { it.date }.distinctBy { it.date }
        val today = todayLocal()
        val todayEntry = entries.find { it.date == today }
        val todaySports = todayEntry?.sports ?: emptyList()
        val allEmpty = todaySports.isEmpty() && entries.all { it.sports.isEmpty() }

        // 1. Today's activities (grouped)
        if (todaySports.isNotEmpty()) {
            container.appendChild(renderTodayActivities(todaySports, todayEntry, userId, onSave))
        }

        // 2. Week overview
        container.appendChild(renderWeekOverview(entries, userId, onSave))

        // 3. Per-sport analytics cards
        container.appendChild(renderAnalytics(entries))

        // 4. Empty state
        if (allEmpty) {
            container.appendChild(renderEmptyState())
        }
    }

    // ─── 1. TODAY'S ACTIVITIES ──────────────────────────────────────────

    private data class SportGroup(
        val key: String,
        val mode: String,
        val sport: Sport,
        val indices: MutableList<Int> = mutableListOf(),
        var totalVal: Double = 0.0
    )

    private fun renderTodayActivities(
        sports: List<SportEntry>,
        todayEntry: Entry?,
        userId: String,
        onSave: () -> Unit
    ): HTMLElement {
        // Group by sport key + mode
        val groups = mutableListOf<SportGroup>()
        sports.forEachIndexed { idx, se ->
            val sp = DataService.sports.find { it.k == se.k } ?: return@forEachIndexed
            val mode = se.mode.ifBlank { sp.u }
            val existing = groups.find { it.key == se.k && it.mode == mode }
            if (existing != null) {
                existing.indices.add(idx)
                existing.totalVal += se.`val`
            } else {
                groups.add(SportGroup(se.k, mode, sp, mutableListOf(idx), se.`val`))
            }
        }
        groups.sortByDescending { it.indices.maxOrNull() ?: 0 }

        // Helper: save to DB + re-render after data loads
        fun saveSilent(mSports: List<SportEntry>, entry: Entry) {
            lastAddedSportKey = null
            // Update local data immediately for instant UI
            val eIdx = DataService.entries.indexOfFirst { it.date == entry.date }
            if (eIdx >= 0) {
                DataService.entries[eIdx] = entry.copy(sports = mSports)
            }
            // Re-render immediately with local data
            MainApp.renderPage()
            // Save to DB in background
            GlobalScope.launch {
                DataService.saveEntry(userId, todayLocal(), entry.weight, mSports, entry.note, entry.id)
                DataService.loadEntries(userId)
            }
        }

        // Helper: save + full re-render (for delete which changes structure)
        fun saveAndRender(mSports: List<SportEntry>, entry: Entry) {
            lastAddedSportKey = null
            GlobalScope.launch {
                DataService.saveEntry(userId, todayLocal(), entry.weight, mSports, entry.note, entry.id)
                DataService.loadEntries(userId)
                onSave()
            }
        }

        return div {
            style.cssText = Styles.CARD

            // Header with settings shortcut
            appendChild(div {
                style.cssText = "margin-bottom:12px;display:flex;align-items:center;gap:10px"
                appendChild(div {
                    style.cssText = "$SECTION_TITLE;flex:1"
                    textContent = t("today_title")
                })
                val cnt = sports.size
                appendChild(span("$cnt ${if (cnt == 1) t("activity_singular") else if (cnt < 5) t("activity_few") else t("activity_many")}") {
                    style.cssText = "font-size:11px;color:${Styles.TEXT_30};font-weight:600"
                })
                // Share today's activities
                appendChild(ShareCard.shareButton {
                    ShareCard.shareActivities(sports, todayEntry?.date ?: todayLocal())
                })
                // Settings gear → jump to activity settings
                appendChild(button("\u2699") {
                    style.cssText = "width:28px;height:28px;border-radius:8px;border:none;background:transparent;color:${Styles.TEXT_20};font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                    onclick = {
                        SettingsTab.jumpToActivities()
                        MainApp.navigateTo("sett")
                        Unit
                    }
                })
            })

            // Compact grouped activities — tap to expand, state persisted
            groups.forEach { g ->
                val c = Styles.adaptColor(g.sport.color.ifEmpty { Styles.A1 })
                // Resolve unit: try mode, then sport default, skip UUIDs
                val rawUnit = g.mode.ifBlank { g.sport.u }
                val unitStr = I18n.tU(rawUnit).let { if (it.length > 10 || it.contains("-")) I18n.tU(g.sport.u) else it }
                val isHighlighted = lastAddedSportKey == g.key
                val totalStr = if (g.totalVal == kotlin.math.floor(g.totalVal)) fmt0(g.totalVal) else fmt1(g.totalVal)
                val countStr = if (g.indices.size > 1) "${g.indices.size}x" else ""
                val groupKey = "${g.key}|${g.mode}"
                val isExpanded = expandedGroups.contains(groupKey)

                // Detail panel
                val detailDiv = div { style.cssText = "display:${if (isExpanded) "block" else "none"};padding:6px 0 2px 0" }

                // Detail rows
                g.indices.forEach { idx ->
                    val se = sports[idx]
                    val v = se.`val`
                    val inputId = "val-${g.key}-$idx"
                    val totalId = "total-${g.key}-${g.mode}"

                    fun fv(d: Double): String = if (d == kotlin.math.floor(d)) fmt0(d) else fmt1(d)

                    // Update input + header total in place after +/−
                    fun updateInPlace(newVal: Double, mSports: List<SportEntry>) {
                        // Update this input
                        (kotlinx.browser.document.getElementById(inputId) as? HTMLInputElement)?.value = fv(newVal)
                        // Update group total
                        val newTotal = mSports.filter { it.k == g.key && (it.mode.ifBlank { DataService.sports.find { s -> s.k == g.key }?.u ?: "" }) == g.mode }.sumOf { it.`val` }
                        (kotlinx.browser.document.getElementById(totalId) as? HTMLElement)?.textContent = fv(newTotal)
                    }

                    detailDiv.appendChild(div {
                        style.cssText = "display:grid;grid-template-columns:24px 26px 56px 30px 26px 1fr 24px;align-items:center;gap:4px;padding:4px 0 4px 8px"
                        // dot
                        appendChild(div {
                            style.cssText = "display:flex;align-items:center;justify-content:center"
                            appendChild(div { style.cssText = "width:4px;height:4px;border-radius:2px;background:${c}40" })
                        })
                        // −
                        appendChild(button("\u2212") {
                            style.cssText = "width:26px;height:26px;border-radius:8px;border:none;background:${Styles.INPUT_BG};border:1.5px solid ${Styles.CARD_BORDER};color:${Styles.TEXT_SEC};font-size:14px;font-weight:900;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                            onclick = {
                                val entry = DataService.entries.find { it.date == todayLocal() }
                                if (entry != null) {
                                    val mSports = entry.sports.toMutableList()
                                    if (idx < mSports.size) {
                                        val cur = mSports[idx]
                                        val unit = DataService.units.find { it.key == g.mode }
                                        val stp = if (unit != null && unit.threshold > 0 && cur.`val` > unit.threshold) unit.stepAbove else unit?.step ?: 1.0
                                        val nv = (cur.`val` - stp).coerceAtLeast(0.0)
                                        mSports[idx] = SportEntry(cur.k, cur.mode, nv)
                                        updateInPlace(nv, mSports)
                                        saveSilent(mSports, entry)
                                    }
                                }
                                Unit
                            }
                        })
                        // Value
                        appendChild(input("number") {
                            id = inputId
                            style.cssText = "width:100%;background:${Styles.TEXT_06};border:1.5px solid ${Styles.CARD_BORDER};border-radius:8px;padding:4px 4px;color:${Styles.TEXT};font-size:15px;font-weight:800;text-align:center;outline:none;font-family:inherit;-moz-appearance:textfield;box-sizing:border-box"
                            value = fv(v)
                            onchange = { e ->
                                val nv = (e.target as HTMLInputElement).value.toDoubleOrNull() ?: v
                                val entry = DataService.entries.find { it.date == todayLocal() }
                                if (entry != null) {
                                    val mSports = entry.sports.toMutableList()
                                    if (idx < mSports.size) {
                                        mSports[idx] = SportEntry(mSports[idx].k, mSports[idx].mode, nv.coerceAtLeast(0.0))
                                        updateInPlace(nv.coerceAtLeast(0.0), mSports)
                                        saveSilent(mSports, entry)
                                    }
                                }
                                Unit
                            }
                        })
                        // Unit
                        appendChild(span(unitStr) {
                            style.cssText = "font-size:11px;color:${Styles.TEXT_30};font-weight:600"
                        })
                        // +
                        appendChild(button("+") {
                            style.cssText = "width:26px;height:26px;border-radius:8px;border:none;background:${Styles.INPUT_BG};border:1.5px solid ${Styles.CARD_BORDER};color:${Styles.TEXT_SEC};font-size:14px;font-weight:900;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                            onclick = {
                                val entry = DataService.entries.find { it.date == todayLocal() }
                                if (entry != null) {
                                    val mSports = entry.sports.toMutableList()
                                    if (idx < mSports.size) {
                                        val cur = mSports[idx]
                                        val unit = DataService.units.find { it.key == g.mode }
                                        val stp = if (unit != null && unit.threshold > 0 && cur.`val` >= unit.threshold) unit.stepAbove else unit?.step ?: 1.0
                                        val nv = cur.`val` + stp
                                        mSports[idx] = SportEntry(cur.k, cur.mode, nv)
                                        updateInPlace(nv, mSports)
                                        saveSilent(mSports, entry)
                                    }
                                }
                                Unit
                            }
                        })
                        // spacer
                        appendChild(div {})
                        // ✕ (delete needs full re-render)
                        appendChild(button("\u2715") {
                            style.cssText = "width:24px;height:24px;border-radius:6px;border:none;background:transparent;color:${Styles.TEXT_30};font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                            onclick = {
                                expandedGroups.add(groupKey)
                                val entry = DataService.entries.find { it.date == todayLocal() }
                                if (entry != null) {
                                    val mSports = entry.sports.toMutableList()
                                    if (idx < mSports.size) {
                                        mSports.removeAt(idx)
                                        saveAndRender(mSports, entry)
                                    }
                                }
                                Unit
                            }
                        })
                    })
                }

                // Summary row — grid aligned columns: [icon] [name] [count] [value] [unit]
                appendChild(div {
                    style.cssText = "cursor:pointer;${if (isHighlighted) "background:${Styles.YELLOW}08;border-radius:10px;padding:0 8px;margin:0 -8px" else ""}"

                    val headerRow = div {
                        style.cssText = "display:grid;grid-template-columns:24px 1fr auto 56px 30px;align-items:center;gap:4px;padding:9px 0"
                        appendChild(span(g.sport.i) { style.cssText = "font-size:18px;line-height:1;text-align:center" })
                        appendChild(span(g.sport.localName()) {
                            style.cssText = "font-size:14px;font-weight:600;color:$c;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
                        })
                        appendChild(span(countStr) {
                            style.cssText = "font-size:10px;font-weight:700;color:${Styles.TEXT_TER};text-align:right;padding-right:4px"
                        })
                        appendChild(span(totalStr) {
                            id = "total-${g.key}-${g.mode}"
                            style.cssText = "font-size:17px;font-weight:800;color:${Styles.TEXT};text-align:right"
                        })
                        appendChild(span(unitStr) {
                            style.cssText = "font-size:11px;color:${Styles.TEXT_30};font-weight:600"
                        })
                    }
                    appendChild(headerRow)

                    headerRow.onclick = { _ ->
                        if (expandedGroups.contains(groupKey)) expandedGroups.remove(groupKey) else expandedGroups.add(groupKey)
                        MainApp.renderPage()
                        Unit
                    }

                    appendChild(detailDiv)
                })
            }
        }
    }

    // ─── 2. WEEK OVERVIEW ──────────────────────────────────────────────

    private fun renderWeekOverview(entries: List<Entry>, userId: String, onSave: () -> Unit): HTMLElement {
        val now = Date()
        val dayOfWeek = ((now.getDay() + 6) % 7)
        val today = todayLocal()
        val dayNames = listOf(t("day_mon"), t("day_tue"), t("day_wed"), t("day_thu"), t("day_fri"), t("day_sat"), t("day_sun"))

        // Compute week start as YYYY-MM-DD string (avoids timezone issues with Date comparison)
        val wkStartDate = Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek)
        val wkStartStr = "${wkStartDate.getFullYear()}-${(wkStartDate.getMonth() + 1).toString().padStart(2, '0')}-${wkStartDate.getDate().toString().padStart(2, '0')}"

        val activeDays = mutableSetOf<String>()
        data class WeekSportAgg(val sport: Sport, val mode: String, var total: Double = 0.0)
        val weekSportsMap = LinkedHashMap<String, WeekSportAgg>()
        data class DaySportGroup(val sport: Sport, val mode: String, val entries: MutableList<SportEntry> = mutableListOf(), var total: Double = 0.0)
        val dayGrouped = mutableMapOf<String, MutableMap<String, DaySportGroup>>()

        entries.forEach { e ->
            // Compare date strings (YYYY-MM-DD sorts correctly)
            if (e.date >= wkStartStr && e.date <= today) {
                if (e.sports.isNotEmpty()) activeDays.add(e.date)
                e.sports.forEach { s ->
                    val sp = DataService.sports.find { it.k == s.k } ?: return@forEach
                    val mode = s.mode.ifBlank { sp.u }
                    val wKey = "${s.k}|$mode"
                    val wa = weekSportsMap.getOrPut(wKey) { WeekSportAgg(sp, mode) }
                    wa.total += s.`val`
                    val dg = dayGrouped.getOrPut(e.date) { LinkedHashMap() }
                    val dga = dg.getOrPut(wKey) { DaySportGroup(sp, mode) }
                    dga.entries.add(s)
                    dga.total += s.`val`
                }
            }
        }

        return div {
            style.cssText = Styles.CARD

            // Header — fixed 32px height
            appendChild(div {
                style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;height:32px"
                if (selectedDayFilter != null) {
                    appendChild(button("") {
                        style.cssText = "width:32px;height:32px;border-radius:10px;border:none;background:${Styles.YELLOW}18;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                        innerHTML = "<svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='${Styles.YELLOW}' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><path d='M15 18l-6-6 6-6'/></svg>"
                        onclick = {
                            selectedDayFilter = null
                            MainApp.renderPage()
                            Unit
                        }
                    })
                    appendChild(span(formatDateFull(selectedDayFilter!!)) {
                        style.cssText = "font-size:15px;font-weight:800;color:${Styles.TEXT};letter-spacing:1px;margin-left:8px"
                    })
                    appendChild(div { style.cssText = "flex:1" })
                } else {
                    appendChild(span(t("this_week_title")) {
                        style.cssText = SECTION_TITLE
                    })
                }
                // Activity count badge
                val cnt = if (selectedDayFilter != null) {
                    dayGrouped[selectedDayFilter]?.values?.sumOf { it.entries.size } ?: 0
                } else activeDays.size
                appendChild(span("$cnt") {
                    style.cssText = "font-size:11px;font-weight:700;color:${Styles.TEXT_TER};padding:3px 8px;border-radius:6px;background:${Styles.TEXT_04}"
                })
            })

            // Day dots (clickable)
            appendChild(div {
                style.cssText = "display:flex;gap:6px;margin-bottom:16px"
                for (di in 0..6) {
                    val dd = Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek + di)
                    val ds = "${dd.getFullYear()}-${(dd.getMonth() + 1).toString().padStart(2, '0')}-${dd.getDate().toString().padStart(2, '0')}"
                    val hasAct = ds in activeDays
                    val isFuture = ds > today
                    val isToday = ds == today
                    val isSelected = selectedDayFilter == ds

                    appendChild(div {
                        style.cssText = "flex:1;text-align:center;cursor:${if (!isFuture) "pointer" else "default"}"
                        if (!isFuture) {
                            onclick = { selectedDayFilter = if (selectedDayFilter == ds) null else ds; MainApp.renderPage(); Unit }
                        }
                        appendChild(div {
                            style.cssText = "font-size:11px;color:${when {
                                isSelected -> "${Styles.TEXT}"
                                isFuture -> "${Styles.TEXT_15}"
                                isToday -> Styles.adaptColor("#fb7185")
                                else -> Styles.adaptColor("#818cf8")
                            }};font-weight:700;margin-bottom:4px;letter-spacing:0.5px"
                            textContent = dayNames[di]
                        })
                        appendChild(div {
                            style.cssText = "height:34px;border-radius:10px;border:${when {
                                isSelected -> "2px solid #818cf8"
                                hasAct -> "1.5px solid #818cf850"
                                isFuture -> "1.5px solid ${Styles.TEXT_04}"
                                else -> "1.5px solid ${Styles.TEXT_08}"
                            }};background:${when {
                                isSelected -> "linear-gradient(135deg,#818cf840,#818cf820)"
                                hasAct -> "#818cf810"
                                else -> "transparent"
                            }};display:flex;align-items:center;justify-content:center;transition:all 0.2s"
                            appendChild(span("${dd.getDate()}") {
                                style.cssText = "font-size:15px;font-weight:800;color:${when {
                                    isSelected -> "${Styles.TEXT}"
                                    isFuture -> "${Styles.TEXT_10}"
                                    isToday -> Styles.adaptColor("#fb7185")
                                    else -> "${Styles.TEXT_SEC}"
                                }}"
                            })
                        })
                    })
                }
            })

            // ── DAY VIEW: grouped activities ──
            if (selectedDayFilter != null) {
                val dayGroups = dayGrouped[selectedDayFilter!!]
                if (dayGroups != null && dayGroups.isNotEmpty()) {
                    dayGroups.forEach { (_, grp) ->
                        val c = Styles.adaptColor(grp.sport.color.ifEmpty { Styles.A1 })
                        val count = grp.entries.size
                        val valStr = if (grp.total == kotlin.math.floor(grp.total)) fmt0(grp.total) else fmt1(grp.total)

                        appendChild(div {
                            style.cssText = "background:${c}08;border:1.5px solid ${c}15;border-radius:14px;padding:10px 12px;margin-bottom:8px"

                            // Single row: icon + name + count badge + total
                            appendChild(div {
                                style.cssText = "display:flex;align-items:center;gap:8px"
                                appendChild(span(grp.sport.i) { style.cssText = "font-size:20px;line-height:1" })
                                appendChild(span(grp.sport.localName()) {
                                    style.cssText = "flex:1;font-size:14px;font-weight:700;color:$c"
                                })
                                if (count > 1) {
                                    appendChild(span("${count}x") {
                                        style.cssText = "font-size:10px;font-weight:700;color:${Styles.TEXT_30};margin-right:4px"
                                    })
                                }
                                appendChild(span(valStr) {
                                    style.cssText = "font-size:18px;font-weight:900;color:${Styles.TEXT}"
                                })
                                appendChild(span(I18n.tU(grp.mode)) {
                                    style.cssText = "font-size:10px;color:${Styles.TEXT_35};font-weight:700;margin-left:3px"
                                })
                            })
                        })
                    }
                } else {
                    appendChild(div {
                        style.cssText = "text-align:center;padding:20px 0;color:${Styles.TEXT_20};font-size:13px;font-weight:600"
                        textContent = t("no_activities_this_day")
                    })
                }
            } else {
                // ── WEEK VIEW: grouped progress bars ──
                appendChild(div { style.cssText = "height:8px" })

                if (weekSportsMap.isNotEmpty()) {
                    // Sort: most recently added today first, then by total descending
                    val todaySports = entries.find { it.date == today }?.sports ?: emptyList()
                    val todayOrder = todaySports.mapIndexed { i, s -> "${s.k}|${s.mode.ifBlank { DataService.sports.find { sp -> sp.k == s.k }?.u ?: "" }}" to i }
                        .reversed().distinctBy { it.first }.map { it.first }
                    val sortedWeek = weekSportsMap.entries.sortedWith(compareBy<Map.Entry<String, WeekSportAgg>> {
                        val idx = todayOrder.indexOf(it.key)
                        if (idx >= 0) idx else Int.MAX_VALUE
                    }.thenByDescending { it.value.total })
                    // Compute weekly average per sport from past 18 weeks
                    // Group past entries by calendar week, then average the non-zero weeks
                    val pastWeeks = 18
                    val cutDate = Date(now.getFullYear(), now.getMonth(), now.getDate() - pastWeeks * 7)
                    val cutStr = "${cutDate.getFullYear()}-${(cutDate.getMonth() + 1).toString().padStart(2, '0')}-${cutDate.getDate().toString().padStart(2, '0')}"

                    // sportKey -> Map<weekNumber, total>
                    val pastWeekTotals = mutableMapOf<String, MutableMap<Int, Double>>()
                    val thisMondayMs = Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek).getTime()
                    entries.forEach { e ->
                        if (e.date >= wkStartStr || e.date < cutStr) return@forEach
                        // Calculate which calendar week (Mon-Sun) this entry belongs to
                        val ed = Date(e.date + "T12:00:00")
                        val edDow = ((ed.getDay() + 6) % 7)
                        val entryMondayMs = Date(ed.getFullYear(), ed.getMonth(), ed.getDate() - edDow).getTime()
                        val wn = ((thisMondayMs - entryMondayMs) / (7 * 86400000.0) + 0.01).toInt()
                        if (wn < 1) return@forEach // skip current week

                        e.sports.forEach { s ->
                            val key = "${s.k}|${s.mode.ifBlank { DataService.sports.find { sp -> sp.k == s.k }?.u ?: "" }}"
                            val weekMap = pastWeekTotals.getOrPut(key) { mutableMapOf() }
                            weekMap[wn] = (weekMap[wn] ?: 0.0) + s.`val`
                        }
                    }

                    // Calculate average from non-zero weeks only
                    val avgBySport = mutableMapOf<String, Double>()
                    pastWeekTotals.forEach { (key, weekMap) ->
                        val nonZero = weekMap.values.filter { it > 0 }
                        avgBySport[key] = if (nonZero.isNotEmpty()) nonZero.sum() / nonZero.size else 0.0
                    }

                    sortedWeek.forEach { (wKey, wa) ->
                        val c = Styles.adaptColor(wa.sport.color.ifEmpty { Styles.A1 })
                        val avg = avgBySport[wKey] ?: 0.0
                        // Bar: 100% width = 150% of average (so average shows as 2/3 full)
                        // No history = full bar
                        val ratio = if (avg > 0) wa.total / avg else 1.0
                        val pct = (ratio / 1.5).coerceIn(0.03, 1.0)
                        val valStr = if (wa.total == kotlin.math.floor(wa.total)) fmt0(wa.total) else fmt1(wa.total)

                        val sportKey = wa.sport.k
                        appendChild(div {
                            style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px;cursor:pointer"
                            onclick = {
                                val target = kotlinx.browser.document.getElementById("analytics-$sportKey")
                                if (target != null) {
                                    target.asDynamic().scrollIntoView(js("{behavior:'smooth',block:'center'}"))
                                    val s = target.asDynamic().style
                                    val orig = s.boxShadow ?: ""
                                    // Wait for scroll to finish, then glow: 1.2s delay, 1s fade in, 3s hold, 1s fade out
                                    s.transition = "box-shadow 1s ease-in-out"
                                    kotlinx.browser.window.setTimeout({
                                        s.boxShadow = "0 0 0 3px ${c},0 0 60px ${c}35,0 0 20px ${c}20"
                                        kotlinx.browser.window.setTimeout({
                                            s.transition = "box-shadow 1s ease-in-out"
                                            s.boxShadow = orig
                                        }, 4000)
                                    }, 1200)
                                }
                                Unit
                            }
                            appendChild(div {
                                style.cssText = "width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0"
                                appendChild(span(wa.sport.i) { style.cssText = "font-size:18px;line-height:1" })
                            })
                            appendChild(div {
                                style.cssText = "flex:1;position:relative;height:36px;border-radius:12px;overflow:hidden;background:${Styles.TEXT_04}"
                                appendChild(div {
                                    style.cssText = "position:absolute;top:0;left:0;height:100%;width:${(pct * 100).toInt()}%;border-radius:12px;background:linear-gradient(90deg,${c}30,${c}70);transition:width 0.4s ease"
                                })
                                appendChild(div {
                                    style.cssText = "position:relative;display:flex;align-items:center;justify-content:space-between;height:100%;padding:0 12px;z-index:1"
                                    appendChild(span(wa.sport.localName()) {
                                        style.cssText = "font-size:13px;font-weight:700;color:${Styles.TEXT};text-shadow:${if (ThemeManager.isDark()) "0 1px 2px rgba(0,0,0,0.4)" else "none"}"
                                    })
                                    appendChild(span("$valStr ${I18n.tU(wa.mode)}") {
                                        style.cssText = "font-size:13px;font-weight:700;color:${Styles.TEXT};text-shadow:${if (ThemeManager.isDark()) "0 1px 2px rgba(0,0,0,0.4)" else "none"}"
                                    })
                                })
                            })
                        })
                    }
                } else {
                    appendChild(div {
                        style.cssText = "text-align:center;padding:20px 0;color:${Styles.TEXT_20};font-size:13px;font-weight:600"
                        textContent = t("no_activities_this_week")
                    })
                }
            }
        }
    }

    // ─── 3. PER-SPORT ANALYTICS CARDS ──────────────────────────────────

    private fun renderAnalytics(entries: List<Entry>): HTMLElement {
        val weeksBack = 18
        val now = Date()
        val today = todayLocal()
        val dayOfWeek = ((now.getDay() + 6) % 7)

        // Compute date strings for cutoff and week start (avoids timezone bugs)
        val cutoffDate = Date(now.getFullYear(), now.getMonth(), now.getDate() - weeksBack * 7)
        val cutoffStr = "${cutoffDate.getFullYear()}-${(cutoffDate.getMonth() + 1).toString().padStart(2, '0')}-${cutoffDate.getDate().toString().padStart(2, '0')}"
        val wkStartDate = Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek)
        val wkStartStr = "${wkStartDate.getFullYear()}-${(wkStartDate.getMonth() + 1).toString().padStart(2, '0')}-${wkStartDate.getDate().toString().padStart(2, '0')}"

        data class SportStat(
            var thisWeek: Double = 0.0,
            var total: Double = 0.0,
            var weekTots: MutableMap<Int, Double> = mutableMapOf(),
            var days: MutableSet<String> = mutableSetOf()
        )

        // This week's Monday (for calendar-week-based bar chart)
        val thisMondayDate = Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek)
        val thisMondayMs = thisMondayDate.getTime()

        val stats = mutableMapOf<String, SportStat>()
        entries.forEach { e ->
            if (e.date > today || e.date < cutoffStr) return@forEach
            e.sports.forEach { s ->
                val st = stats.getOrPut(s.k) { SportStat() }
                st.total += s.`val`
                st.days.add(e.date)
                if (e.date >= wkStartStr) st.thisWeek += s.`val`
                // Week number based on calendar weeks (Mon-Sun), not rolling 7-day window
                val ed = Date(e.date + "T12:00:00")
                val edDow = ((ed.getDay() + 6) % 7)
                val entryMondayMs = Date(ed.getFullYear(), ed.getMonth(), ed.getDate() - edDow).getTime()
                val wn = ((thisMondayMs - entryMondayMs) / (7 * 86400000.0) + 0.01).toInt().coerceAtLeast(0)
                st.weekTots[wn] = (st.weekTots[wn] ?: 0.0) + s.`val`
            }
        }

        if (stats.isEmpty()) return div {}

        return div {
            // Section header
            appendChild(div {
                style.cssText = "display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:16px"
                appendChild(div {
                    style.cssText = "text-align:left"
                    appendChild(div {
                        style.cssText = SECTION_TITLE
                        textContent = t("analytics_title")
                    })
                    appendChild(div {
                        style.cssText = "font-size:13px;color:${Styles.TEXT_40};font-weight:700;margin-top:6px"
                        textContent = "${t("analytics_subtitle")} $weeksBack ${t("analytics_weeks")}"
                    })
                })
            })

            // Sort analytics cards to match today's activity order (most recent first)
            val todayEntry = entries.find { it.date == todayLocal() }
            val todaySportOrder = todayEntry?.sports?.mapIndexed { i, s -> s.k to i }
                ?.reversed()?.distinctBy { it.first }?.map { it.first } ?: emptyList()
            val sortedStats = stats.entries.sortedBy { (key, _) ->
                val idx = todaySportOrder.indexOf(key)
                if (idx >= 0) idx else Int.MAX_VALUE
            }

            sortedStats.forEach { (key, st) ->
                val sp = DataService.sports.find { it.k == key } ?: return@forEach
                val c = Styles.adaptColor(sp.color.ifEmpty { Styles.A1 })
                val mode = sp.u

                val weekHist = (weeksBack - 1 downTo 0).map { w -> st.weekTots[w] ?: 0.0 }
                val goalVal = DataService.sportDefs[key]?.gw ?: 0.0
                val dataMax = (weekHist.maxOrNull() ?: 1.0).coerceAtLeast(1.0)
                val histMax = maxOf(dataMax, goalVal) * 1.12
                val isNewBest = st.thisWeek > 0 && st.thisWeek >= (weekHist.maxOrNull() ?: 0.0)

                val pastWeekValues = (1 until weeksBack).map { w -> st.weekTots[w] ?: 0.0 }
                val nonZeroPast = pastWeekValues.filter { it > 0 }
                val avgPerWeek = if (nonZeroPast.isNotEmpty()) nonZeroPast.sum() / nonZeroPast.size else 0.0

                appendChild(div {
                    id = "analytics-$key"
                    val cardAlpha = if (ThemeManager.isDark()) "" else "cc"
                    style.cssText = "border-radius:20px;margin-bottom:28px;background:linear-gradient(160deg,${c}${if (ThemeManager.isDark()) "38" else "25"},${c}${if (ThemeManager.isDark()) "18" else "12"} 40%,${if (ThemeManager.isDark()) "rgba(12,12,18,0.85)" else "rgba(255,255,255,0.92)"} 100%);border:2px solid ${c}${if (ThemeManager.isDark()) "60" else "50"};padding:clamp(12px,2.5vw,16px) clamp(8px,2vw,12px);box-shadow:0 10px 50px ${c}${if (ThemeManager.isDark()) "28" else "18"},0 2px 8px rgba(0,0,0,${if (ThemeManager.isDark()) "0.3" else "0.06"}),inset 0 1px 0 ${c}30;position:relative;overflow:hidden"

                    // Header: icon, name, this week, record badge
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:10px;margin-bottom:0"
                        appendChild(div {
                            style.cssText = "width:48px;height:48px;border-radius:14px;background:linear-gradient(135deg,${c}55,${c}22);border:2px solid ${c}70;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 4px 24px ${c}35"
                            appendChild(span(sp.i) { style.cssText = "font-size:28px" })
                        })
                        appendChild(div {
                            style.cssText = "flex:1"
                            appendChild(span(sp.localName()) {
                                style.cssText = "font-size:22px;font-weight:800;color:${Styles.TEXT};letter-spacing:2px;${if (ThemeManager.isDark()) "text-shadow:0 0 20px ${c}80,0 0 40px ${c}40" else ""}"
                            })
                            appendChild(div {
                                style.cssText = "display:flex;align-items:center;gap:6px;margin-top:4px"
                                val twStr = if (st.thisWeek == kotlin.math.floor(st.thisWeek)) fmt0(st.thisWeek) else fmt1(st.thisWeek)
                                appendChild(span("$twStr ${I18n.tU(mode)}") {
                                    style.cssText = "font-size:14px;color:$c;font-weight:800"
                                })
                                appendChild(span(t("this_week_label")) {
                                    style.cssText = "font-size:11px;color:${c}88;font-weight:600"
                                })
                                if (isNewBest) {
                                    appendChild(span(t("record_badge")) {
                                        style.cssText = "padding:2px 8px;border-radius:6px;font-size:9px;font-weight:900;background:${Styles.YELLOW}18;border:1.5px solid ${Styles.YELLOW}35;color:${Styles.YELLOW};letter-spacing:0.5px"
                                    })
                                }
                            })
                        })
                    })

                    // SVG bar chart
                    val svgW = 960; val svgH = 380
                    val pL2 = 80; val pR2 = 20; val pT2 = 36; val pB2 = 56
                    val cW2 = svgW - pL2 - pR2; val cH2 = svgH - pT2 - pB2
                    val barGap = 4; val nBars = weeksBack
                    val barW = (cW2 - barGap * (nBars - 1)).toDouble() / nBars

                    // Y-axis: nice steps
                    val yStep = when {
                        histMax <= 10 -> 2.0
                        histMax <= 30 -> 5.0
                        histMax <= 100 -> 10.0
                        histMax <= 300 -> 50.0
                        else -> 100.0
                    }

                    val svgContent = buildString {
                        append("<defs>")
                        append("<linearGradient id='bg_$key' x1='0' y1='0' x2='0' y2='1'>")
                        append("<stop offset='0%' stop-color='${Styles.TEXT}' stop-opacity='0.25'/>")
                        append("<stop offset='15%' stop-color='$c' stop-opacity='1'/>")
                        append("<stop offset='100%' stop-color='$c' stop-opacity='0.85'/>")
                        append("</linearGradient>")
                        append("<linearGradient id='bg2_$key' x1='0' y1='0' x2='0' y2='1'>")
                        append("<stop offset='0%' stop-color='$c' stop-opacity='0.7'/>")
                        append("<stop offset='100%' stop-color='$c' stop-opacity='0.3'/>")
                        append("</linearGradient>")
                        append("</defs>")

                        // Y-axis grid + labels
                        var yg = yStep
                        while (yg < histMax) {
                            val gy = pT2 + cH2 - (yg / histMax) * cH2
                            append("<line x1='$pL2' y1='$gy' x2='${svgW - pR2}' y2='$gy' stroke='${Styles.GRID_LINE}' stroke-dasharray='4 6'/>")
                            val label = if (yg == kotlin.math.floor(yg)) fmt0(yg) else fmt1(yg)
                            append("<text x='${pL2 - 12}' y='${gy + 7}' text-anchor='end' fill='${Styles.TEXT_40}' font-size='22' font-weight='600' font-family='system-ui'>$label</text>")
                            yg += yStep
                        }

                        // Bottom line
                        append("<line x1='$pL2' y1='${pT2 + cH2}' x2='${svgW - pR2}' y2='${pT2 + cH2}' stroke='${c}20' stroke-width='1'/>")

                        // Bars
                        val barCenters = mutableListOf<Pair<Double, Double>>()
                        weekHist.forEachIndexed { bi, v ->
                            val isLast = bi == nBars - 1
                            val bx = pL2 + bi * (barW + barGap)
                            val bh = if (histMax > 0 && v > 0) maxOf((v / histMax) * cH2, 3.0) else 0.0
                            val by = pT2 + cH2 - bh
                            val r = minOf(5.0, barW / 2 - 1)
                            val opMin = if (ThemeManager.isDark()) 0.15 else 0.35
                            val opRange = if (ThemeManager.isDark()) 0.35 else 0.45
                            val op = if (isLast) 1.0 else opMin + (bi.toDouble() / (nBars - 1)) * opRange

                            if (bh > 0) {
                                append("<rect x='$bx' y='$by' width='$barW' height='$bh' rx='$r' fill='${if (isLast) "url(#bg_$key)" else "url(#bg2_$key)"}' opacity='$op'/>")
                            }

                            barCenters.add(Pair(bx + barW / 2, if (v > 0) by else (pT2 + cH2).toDouble()))

                            // Current week value above bar - clean, not bold
                            if (isLast && v > 0) {
                                val valY = maxOf(pT2.toDouble() + 16, by - 12)
                                val vStr = if (v == kotlin.math.floor(v)) fmt0(v) else fmt1(v)
                                append("<text x='${bx + barW / 2}' y='$valY' text-anchor='middle' fill='${Styles.TEXT_70}' font-size='22' font-weight='500' font-family='system-ui'>$vStr</text>")
                            }

                            // X-axis labels
                            if (isLast) {
                                append("<text x='${bx + barW / 2}' y='${svgH - 18}' text-anchor='middle' fill='${Styles.TEXT_SEC}' font-size='18' font-weight='500' font-family='system-ui'>${t("chart_this")}</text>")
                                append("<text x='${bx + barW / 2}' y='${svgH - 2}' text-anchor='middle' fill='${Styles.TEXT_SEC}' font-size='18' font-weight='500' font-family='system-ui'>${t("chart_week")}</text>")
                            } else {
                                val wkAgo = nBars - 1 - bi
                                if (wkAgo % 3 == 0 || wkAgo == 1) {
                                    append("<text x='${bx + barW / 2}' y='${svgH - 8}' text-anchor='middle' fill='${Styles.TEXT_TER}' font-size='18' font-weight='500' font-family='system-ui'>-${wkAgo}</text>")
                                }
                            }
                        }

                        // Trend line
                        val nonZero = barCenters.filter { (_, y) -> y < pT2 + cH2 }
                        if (nonZero.size >= 2) {
                            val pts = barCenters.toList()
                            var path = "M${pts[0].first} ${pts[0].second}"
                            for (pi in 1 until pts.size) {
                                val prev = pts[pi - 1]; val cur = pts[pi]
                                path += " C${prev.first + (cur.first - prev.first) * 0.4} ${prev.second},${prev.first + (cur.first - prev.first) * 0.6} ${cur.second},${cur.first} ${cur.second}"
                            }
                            append("<path d='$path' fill='none' stroke='$c' stroke-width='2' stroke-opacity='0.4' stroke-linecap='round'/>")
                        }

                        // Goal line — rendered AFTER bars so it's on top
                        if (goalVal > 0 && histMax > 0) {
                            val glY = pT2 + cH2 - (goalVal / histMax) * cH2
                            val gStr = if (goalVal == kotlin.math.floor(goalVal)) fmt0(goalVal) else fmt1(goalVal)
                            val goalLabel = "${t("chart_goal_badge")} $gStr"
                            val lblW = goalLabel.length * 8 + 16
                            // Dashed line — full width
                            append("<line id='goal-line-$key' x1='${pL2 + lblW + 6}' y1='$glY' x2='${svgW - pR2 - 16}' y2='$glY' stroke='${Styles.YELLOW}' stroke-width='1.5' stroke-dasharray='5 4' stroke-opacity='${if (ThemeManager.isDark()) "0.6" else "1"}'/>")
                            // Label pill — opaque bg
                            append("<rect id='goal-rect-$key' x='$pL2' y='${glY - 13}' width='$lblW' height='26' rx='13' fill='${Styles.BG_DARK}' stroke='${Styles.YELLOW}50' stroke-width='1'/>")
                            append("<rect x='$pL2' y='${glY - 13}' width='$lblW' height='26' rx='13' fill='${Styles.YELLOW}20'/>")
                            append("<text id='goal-text-$key' x='${pL2 + lblW / 2}' y='${glY + 5}' text-anchor='middle' fill='${Styles.YELLOW}' font-size='14' font-weight='800' font-family='system-ui'>$goalLabel</text>")
                            // Drag handle
                            append("<circle id='goal-handle-$key' cx='${svgW - pR2}' cy='$glY' r='8' fill='${Styles.YELLOW}40' stroke='${Styles.YELLOW}' stroke-width='1.5' style='cursor:ns-resize'/>")
                            append("<line id='goal-grip1-$key' x1='${svgW - pR2 - 4}' y1='${glY - 2}' x2='${svgW - pR2 + 4}' y2='${glY - 2}' stroke='${Styles.YELLOW}' stroke-width='1' stroke-opacity='0.7'/>")
                            append("<line id='goal-grip2-$key' x1='${svgW - pR2 - 4}' y1='${glY + 2}' x2='${svgW - pR2 + 4}' y2='${glY + 2}' stroke='${Styles.YELLOW}' stroke-width='1' stroke-opacity='0.7'/>")
                            // Hit area
                            append("<rect x='${pL2 - 5}' y='${glY - 18}' width='${svgW - pL2 - pR2 + 10}' height='36' fill='transparent' style='cursor:ns-resize' class='goal-hit-$key'/>")
                        }
                    }

                    val chartContainer = div {
                        style.cssText = "margin-top:8px;position:relative"
                    }
                    val svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg")
                    svgEl.setAttribute("viewBox", "0 0 $svgW $svgH")
                    svgEl.setAttribute("style", "width:100%;display:block")
                    svgEl.innerHTML = svgContent
                    chartContainer.appendChild(svgEl)

                    // Goal drag: tap to activate → drag → release to save
                    // touch-action stays "auto" until activated, so scrolling works normally
                    if (goalVal > 0) {
                        val sportKey = key
                        val chartHistMax = histMax
                        var isActive = false
                        var isDragging = false
                        var currentGoal = goalVal
                        var deactivateTimer = 0

                        fun highlight(on: Boolean) {
                            svgEl.querySelector("#goal-line-$sportKey")?.setAttribute("stroke-opacity", if (on) "1" else "0.6")
                            svgEl.querySelector("#goal-line-$sportKey")?.setAttribute("stroke-width", if (on) "3" else "1.5")
                            svgEl.querySelector("#goal-handle-$sportKey")?.setAttribute("fill", if (on) Styles.YELLOW else "${Styles.YELLOW}40")
                            svgEl.querySelector("#goal-handle-$sportKey")?.setAttribute("r", if (on) "12" else "8")
                        }

                        fun deactivate() {
                            isActive = false
                            isDragging = false
                            highlight(false)
                            svgEl.asDynamic().style.touchAction = ""
                            if (deactivateTimer != 0) { kotlinx.browser.window.clearTimeout(deactivateTimer); deactivateTimer = 0 }
                        }

                        svgEl.addEventListener("pointerdown", { evt ->
                            val e = evt.asDynamic()
                            val rect = svgEl.asDynamic().getBoundingClientRect()
                            val sy = ((e.clientY as Double) - (rect.top as Double)) / (rect.height as Double) * svgH
                            val goalY = pT2 + cH2 - (currentGoal / chartHistMax) * cH2

                            if (kotlin.math.abs(sy - goalY) < 30) {
                                if (!isActive) {
                                    // First tap: activate (highlight + block scroll + start 5s timer)
                                    isActive = true
                                    highlight(true)
                                    svgEl.asDynamic().style.touchAction = "none"
                                    // Auto-deactivate after 5s if no drag
                                    if (deactivateTimer != 0) kotlinx.browser.window.clearTimeout(deactivateTimer)
                                    deactivateTimer = kotlinx.browser.window.setTimeout({ if (!isDragging) deactivate() }, 5000)
                                    evt.preventDefault()
                                } else {
                                    // Already active: start dragging
                                    isDragging = true
                                    try { svgEl.asDynamic().setPointerCapture(e.pointerId) } catch (_: Exception) {}
                                    evt.preventDefault()
                                }
                            } else if (isActive) {
                                deactivate()
                            }
                        })

                        svgEl.addEventListener("pointermove", { evt ->
                            if (isDragging) {
                                val e = evt.asDynamic()
                                val rect = svgEl.asDynamic().getBoundingClientRect()
                                val sy = ((e.clientY as Double) - (rect.top as Double)) / (rect.height as Double) * svgH
                                val newVal = ((pT2 + cH2 - sy) / cH2 * chartHistMax).coerceIn(0.1, chartHistMax * 2.0)
                                currentGoal = kotlin.math.round(newVal * 10.0) / 10.0
                                val newY = pT2 + cH2 - (currentGoal / chartHistMax) * cH2
                                val gStr = if (currentGoal == kotlin.math.floor(currentGoal)) fmt0(currentGoal) else fmt1(currentGoal)

                                svgEl.querySelector("#goal-line-$sportKey")?.let { it.setAttribute("y1", "$newY"); it.setAttribute("y2", "$newY") }
                                svgEl.querySelector("#goal-rect-$sportKey")?.setAttribute("y", "${newY - 13}")
                                svgEl.querySelector("#goal-rect-$sportKey")?.nextElementSibling?.setAttribute("y", "${newY - 13}")
                                svgEl.querySelector("#goal-text-$sportKey")?.let { it.setAttribute("y", "${newY + 5}"); it.textContent = "${t("chart_goal_badge")} $gStr" }
                                svgEl.querySelector("#goal-handle-$sportKey")?.setAttribute("cy", "$newY")
                                svgEl.querySelector("#goal-grip1-$sportKey")?.let { it.setAttribute("y1", "${newY - 2}"); it.setAttribute("y2", "${newY - 2}") }
                                svgEl.querySelector("#goal-grip2-$sportKey")?.let { it.setAttribute("y1", "${newY + 2}"); it.setAttribute("y2", "${newY + 2}") }
                                svgEl.querySelector(".goal-hit-$sportKey")?.setAttribute("y", "${newY - 18}")
                                evt.preventDefault()
                            }
                        })

                        svgEl.addEventListener("pointerup", { _ ->
                            if (isDragging) {
                                val changed = currentGoal != goalVal && currentGoal > 0
                                deactivate()
                                if (changed) {
                                    DataService.sportDefs.getOrPut(sportKey) { SportDef() }
                                    DataService.sportDefs[sportKey] = DataService.sportDefs[sportKey]!!.copy(gw = currentGoal)
                                    DataService.saveSportDefs()
                                    val scrollY = kotlinx.browser.window.scrollY
                                    MainApp.renderPage()
                                    kotlinx.browser.window.requestAnimationFrame { kotlinx.browser.window.scrollTo(0.0, scrollY) }
                                }
                            }
                        })

                        svgEl.addEventListener("pointercancel", { _ -> deactivate() })
                    }

                    appendChild(chartContainer)

                    // Goal editor (text fallback)
                    appendChild(renderGoalEditor(key, c, mode, goalVal, avgPerWeek))
                })
            }
        }
    }

    private fun renderGoalEditor(sportKey: String, color: String, mode: String, currentGoal: Double, avgPerWeek: Double): HTMLElement {
        return div {
            style.cssText = "margin-top:10px"

            if (currentGoal > 0) {
                // Show current goal with remove option
                style.cssText += ";display:flex;align-items:center;gap:8px;flex-wrap:wrap"
                appendChild(div {
                    style.cssText = "width:20px;height:20px;border-radius:5px;background:${Styles.YELLOW}18;display:flex;align-items:center;justify-content:center;flex-shrink:0"
                    appendChild(span("\u25CE") { style.cssText = "font-size:12px;color:${Styles.YELLOW};line-height:1" })
                })
                val gStr = if (currentGoal == kotlin.math.floor(currentGoal)) fmt0(currentGoal) else fmt1(currentGoal)
                appendChild(span("$gStr ${I18n.tU(mode)}/${t("chart_week")}") {
                    style.cssText = "font-size:13px;color:${Styles.TEXT_70};font-weight:600"
                })
                appendChild(button("\u2715") {
                    style.cssText = "width:22px;height:22px;border-radius:6px;border:none;background:rgba(251,113,133,0.12);color:#fb7185;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                    onclick = {
                        DataService.sportDefs.getOrPut(sportKey) { SportDef() }
                        DataService.sportDefs[sportKey] = DataService.sportDefs[sportKey]!!.copy(gw = 0.0)
                        DataService.saveSportDefs()
                        val scrollY = kotlinx.browser.window.scrollY
                        MainApp.renderPage()
                        kotlinx.browser.window.requestAnimationFrame { kotlinx.browser.window.scrollTo(0.0, scrollY) }
                        Unit
                    }
                })
            } else {
                // No goal yet - show add button that toggles to input
                val displayDiv = div {
                    style.cssText = "display:flex;align-items:center"
                    appendChild(button("\u25CE ${t("chart_goal_badge")}") {
                        style.cssText = "padding:5px 14px;border-radius:10px;border:1.5px solid ${color}20;background:${color}${if (ThemeManager.isDark()) "06" else "15"};color:$color;font-size:11px;font-weight:700;cursor:pointer;letter-spacing:0.5px;font-family:inherit"
                    })
                }

                val inp = input("number") {
                    style.cssText = "width:80px;background:${Styles.INPUT_BG};border:1.5px solid ${color}40;border-radius:10px;padding:6px 10px;color:${Styles.TEXT};font-size:14px;font-weight:700;text-align:center;outline:none;font-family:inherit"
                    placeholder = if (avgPerWeek > 0) {
                        if (avgPerWeek == kotlin.math.floor(avgPerWeek)) fmt0(avgPerWeek) else fmt1(avgPerWeek)
                    } else ""
                    step = "0.1"
                }

                val saveBtn = button("\u2713") {
                    style.cssText = "width:28px;height:28px;border-radius:8px;border:none;background:$color;color:#000;font-size:14px;font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                    onclick = {
                        val nv = inp.value.toDoubleOrNull() ?: 0.0
                        if (nv > 0) {
                            DataService.sportDefs.getOrPut(sportKey) { SportDef() }
                            DataService.sportDefs[sportKey] = DataService.sportDefs[sportKey]!!.copy(gw = nv)
                            DataService.saveSportDefs()
                            val scrollY = kotlinx.browser.window.scrollY
                            MainApp.renderPage()
                            kotlinx.browser.window.requestAnimationFrame { kotlinx.browser.window.scrollTo(0.0, scrollY) }
                        }
                        Unit
                    }
                }

                val cancelBtn = button("\u2715") {
                    style.cssText = "width:28px;height:28px;border-radius:8px;border:1.5px solid ${Styles.CARD_BORDER};background:transparent;color:${Styles.TEXT_40};font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                }

                val editDiv = div {
                    style.cssText = "display:none;align-items:center;gap:8px"
                    appendChild(inp)
                    appendChild(span(I18n.tU(mode)) {
                        style.cssText = "font-size:11px;color:${Styles.TEXT_40};font-weight:600"
                    })
                    appendChild(saveBtn)
                    appendChild(cancelBtn)
                }

                // Wire onclick after both divs exist
                displayDiv.onclick = {
                    displayDiv.style.display = "none"
                    editDiv.style.display = "flex"
                    Unit
                }
                cancelBtn.onclick = {
                    editDiv.style.display = "none"
                    displayDiv.style.display = "flex"
                    Unit
                }

                appendChild(displayDiv)
                appendChild(editDiv)
            }
        }
    }

    // ─── 4. ADD ACTIVITY SECTION ───────────────────────────────────────

    private fun getEnabledSports(): List<Sport> {
        return DataService.sports.filter { sp ->
            val def = DataService.sportDefs[sp.k]
            def?.on ?: sp.isDefault
        }
    }

    private fun getQuickValues(unit: String): List<Double> {
        return when (unit) {
            "min" -> listOf(10.0, 15.0, 20.0, 30.0, 45.0, 60.0, 90.0)
            "hod" -> listOf(0.5, 1.0, 1.5, 2.0, 3.0)
            "km" -> listOf(1.0, 2.0, 3.0, 5.0, 10.0, 15.0, 20.0)
            "ks" -> listOf(5.0, 10.0, 15.0, 20.0, 30.0, 50.0)
            "sety" -> listOf(1.0, 2.0, 3.0, 4.0, 5.0, 6.0)
            "kola" -> listOf(1.0, 2.0, 3.0, 5.0, 10.0)
            "sedenia" -> listOf(1.0, 2.0, 3.0, 5.0)
            "strany" -> listOf(5.0, 10.0, 20.0, 30.0, 50.0, 100.0)
            "litre" -> listOf(0.5, 1.0, 1.5, 2.0, 3.0)
            "sek" -> listOf(15.0, 30.0, 45.0, 60.0, 90.0, 120.0)
            else -> listOf(5.0, 10.0, 15.0, 20.0, 30.0, 60.0)
        }
    }

    private fun renderAddActivity(userId: String, onSave: () -> Unit): HTMLElement {
        val sp = selectedSportForAdd
        if (sp != null) {
            return renderValueEntry(sp, userId, onSave)
        }
        return renderSportPicker(userId, onSave)
    }

    private fun renderSportPicker(userId: String, onSave: () -> Unit): HTMLElement {
        val enabledSports = getEnabledSports()
        var searchQuery = ""

        val gridDiv = div { style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:2px" }

        fun rebuildGrid() {
            gridDiv.clear()
            val filtered = if (searchQuery.isBlank()) enabledSports
                else enabledSports.filter { it.localName().lowercase().contains(searchQuery.lowercase()) || it.l.lowercase().contains(searchQuery.lowercase()) || it.i.contains(searchQuery) }

            if (filtered.isEmpty()) {
                gridDiv.style.cssText = "padding:20px 0;text-align:center"
                gridDiv.appendChild(span(t("diary_no_results")) {
                    style.cssText = "font-size:13px;color:${Styles.TEXT_TER}"
                })
            } else {
                gridDiv.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:2px"
                filtered.forEach { sport ->
                    gridDiv.appendChild(button("") {
                        style.cssText = "padding:8px 10px;border-radius:8px;border:none;background:transparent;cursor:pointer;display:flex;align-items:center;gap:6px"
                        appendChild(span(sport.i) { style.cssText = "font-size:16px;line-height:1;flex-shrink:0" })
                        appendChild(span(sport.localName()) {
                            style.cssText = "font-size:13px;font-weight:600;color:${Styles.TEXT_60};overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
                        })
                        onclick = {
                            selectedSportForAdd = sport
                            MainApp.renderPage()
                            Unit
                        }
                    })
                }
            }
        }

        return div {
            style.cssText = Styles.CARD

            // Header
            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:12px"
                appendChild(span(t("add_activity_title")) {
                    style.cssText = SECTION_TITLE
                })
                appendChild(button("\u2699") {
                    style.cssText = "width:24px;height:24px;border-radius:6px;border:none;background:transparent;color:${Styles.TEXT_20};font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                    onclick = {
                        SettingsTab.jumpToActivities()
                        MainApp.navigateTo("sett")
                        Unit
                    }
                })
                appendChild(div { style.cssText = "flex:1" })
                appendChild(button("\u2715") {
                    style.cssText = "width:28px;height:28px;border-radius:8px;border:none;background:${Styles.TEXT_05};color:${Styles.TEXT_30};font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                    onclick = {
                        MainApp.showTrainForm = false
                        selectedSportForAdd = null
                        MainApp.renderPage()
                        Unit
                    }
                })
            })

            // Search
            if (enabledSports.size > 6) {
                appendChild(input("text") {
                    style.cssText = "width:100%;padding:10px 14px;border-radius:10px;border:none;background:${Styles.TEXT_05};color:${Styles.TEXT};font-size:13px;font-weight:500;outline:none;box-sizing:border-box;font-family:inherit;margin-bottom:12px"
                    placeholder = t("diary_search_placeholder")
                    oninput = { e ->
                        searchQuery = (e.target as HTMLInputElement).value
                        rebuildGrid()
                        Unit
                    }
                })
            }

            if (enabledSports.isEmpty()) {
                appendChild(div {
                    style.cssText = "text-align:center;padding:24px 0;color:${Styles.TEXT_30};font-size:14px"
                    textContent = t("no_data_title")
                })
            } else {
                rebuildGrid()
                appendChild(gridDiv)
            }
        }
    }

    private fun renderValueEntry(sp: Sport, userId: String, onSave: () -> Unit): HTMLElement {
        val c = Styles.adaptColor(sp.color.ifEmpty { Styles.A1 })
        val unit = sp.u
        val quickValues = getQuickValues(unit)
        var accumulated = 0.0

        fun saveActivity(value: Double) {
            GlobalScope.launch {
                val today = todayLocal()
                val existing = DataService.entries.find { it.date == today }
                val currentSports = existing?.sports?.toMutableList() ?: mutableListOf()
                currentSports.add(SportEntry(sp.k, sp.u, value))
                DataService.saveEntry(userId, today, existing?.weight ?: 0.0, currentSports, existing?.note ?: "", existing?.id)
                DataService.loadEntries(userId)
                lastAddedSportKey = sp.k
                selectedSportForAdd = null
                onSave()
                // Clear highlight after 3 seconds
                kotlinx.browser.window.setTimeout({ lastAddedSportKey = null }, 3000)
            }
        }

        fun fmtVal(v: Double): String = if (v == kotlin.math.floor(v)) fmt0(v) else fmt1(v)

        // The single input element - referenced by quick picks and save
        val valueInput = input("number") {
            id = "add-value-input"
            style.cssText = "background:transparent;border:none;outline:none;color:$c;font-size:48px;font-weight:900;text-align:center;width:100%;font-family:inherit;letter-spacing:-1px;-moz-appearance:textfield"
            value = "0"
            placeholder = "0"
            oninput = { e ->
                accumulated = ((e.target as HTMLInputElement).value.toDoubleOrNull() ?: 0.0).coerceAtLeast(0.0)
                // Update save button
                val saveBtn = kotlinx.browser.document.getElementById("add-save-btn") as? HTMLElement
                if (saveBtn != null) {
                    saveBtn.style.opacity = if (accumulated > 0) "1" else "0.3"
                    saveBtn.style.asDynamic().pointerEvents = if (accumulated > 0) "auto" else "none"
                }
                Unit
            }
        }

        fun syncInput() {
            accumulated = accumulated.coerceAtLeast(0.0)
            valueInput.value = fmtVal(accumulated)
            valueInput.dispatchEvent(org.w3c.dom.events.Event("input"))
        }

        return div {
            style.cssText = Styles.CARD

            // Header: back + sport info
            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:12px;margin-bottom:24px"
                appendChild(button("") {
                    style.cssText = "width:36px;height:36px;border-radius:18px;border:none;background:${Styles.TEXT_06};color:${Styles.TEXT_40};cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                    innerHTML = "<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M19 12H5'/><path d='M12 19l-7-7 7-7'/></svg>"
                    onclick = {
                        selectedSportForAdd = null
                        MainApp.renderPage()
                        Unit
                    }
                })
                appendChild(div {
                    style.cssText = "width:44px;height:44px;border-radius:14px;background:${c}15;border:1.5px solid ${c}30;display:flex;align-items:center;justify-content:center;flex-shrink:0"
                    appendChild(span(sp.i) { style.cssText = "font-size:24px" })
                })
                appendChild(span(sp.localName()) {
                    style.cssText = "font-size:18px;font-weight:800;color:${Styles.TEXT}"
                })
            })

            // Single value field - input + unit label
            appendChild(div {
                style.cssText = "display:flex;align-items:baseline;justify-content:center;gap:8px;padding:20px 0 24px;border-bottom:1px solid ${Styles.TEXT_06};margin-bottom:20px"
                appendChild(valueInput)
                appendChild(span(I18n.tU(unit)) {
                    style.cssText = "font-size:20px;font-weight:700;color:${Styles.TEXT_35}"
                })
            })

            // Plus pills
            appendChild(div {
                style.cssText = "display:flex;gap:5px;flex-wrap:wrap;margin-bottom:10px"
                quickValues.forEach { qv ->
                    appendChild(button("+${fmtVal(qv)}") {
                        style.cssText = "padding:8px 14px;border-radius:10px;border:none;background:${c}${if (ThemeManager.isDark()) "10" else "18"};color:$c;border:1.5px solid ${c}${if (ThemeManager.isDark()) "00" else "20"};font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;transition:all 0.12s"
                        onclick = {
                            accumulated += qv
                            syncInput()
                            this.style.background = "${c}30"
                            val btn = this
                            kotlinx.browser.window.setTimeout({ btn.style.background = "${c}10" }, 150)
                            Unit
                        }
                    })
                }
            })
            // Minus pills + Reset
            appendChild(div {
                style.cssText = "display:flex;gap:5px;flex-wrap:wrap;margin-bottom:20px"
                quickValues.forEach { qv ->
                    appendChild(button("\u2212${fmtVal(qv)}") {
                        style.cssText = "padding:8px 14px;border-radius:10px;border:none;background:rgba(251,113,133,${if (ThemeManager.isDark()) "0.06" else "0.12"});color:rgba(251,113,133,${if (ThemeManager.isDark()) "0.5" else "0.7"});font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;transition:all 0.12s"
                        onclick = {
                            accumulated -= qv
                            syncInput()
                            this.style.background = "rgba(251,113,133,0.18)"
                            val btn = this
                            kotlinx.browser.window.setTimeout({ btn.style.background = "rgba(251,113,133,0.06)" }, 150)
                            Unit
                        }
                    })
                }
                // Clear
                appendChild(button("\u232B") {
                    style.cssText = "padding:8px 14px;border-radius:10px;border:none;background:${Styles.CARD_BG};color:${Styles.TEXT_SEC};font-size:16px;cursor:pointer;font-family:inherit"
                    onclick = {
                        accumulated = 0.0
                        syncInput()
                        Unit
                    }
                })
            })

            // Save + Cancel
            appendChild(div {
                style.cssText = "display:flex;gap:10px"
                appendChild(button(t("btn_save")) {
                    id = "add-save-btn"
                    style.cssText = "flex:1;padding:15px;border-radius:14px;border:none;background:$c;color:#fff;font-size:16px;font-weight:800;cursor:pointer;letter-spacing:0.5px;opacity:0.3;pointer-events:none;transition:opacity 0.2s"
                    onclick = {
                        if (accumulated > 0) {
                            saveActivity(accumulated)
                        }
                        Unit
                    }
                })
                appendChild(button(t("btn_cancel")) {
                    style.cssText = Styles.BTN_SECONDARY
                    onclick = {
                        selectedSportForAdd = null
                        MainApp.renderPage()
                        Unit
                    }
                })
            })
        }
    }

    // ─── 5. EMPTY STATE ────────────────────────────────────────────────

    private fun renderEmptyState(): HTMLElement {
        return div {
            style.cssText = "${Styles.CARD};text-align:center;padding:clamp(32px,6vw,48px) clamp(16px,3vw,24px)"
            appendChild(span("\u26A1") { style.cssText = "font-size:48px;display:block;margin-bottom:16px" })
            appendChild(div {
                style.cssText = "font-size:18px;font-weight:800;color:${Styles.TEXT};margin-bottom:8px"
                textContent = t("no_data_title")
            })
            appendChild(div {
                style.cssText = "font-size:14px;color:${Styles.TEXT_45};line-height:1.6;margin-bottom:20px"
                textContent = t("no_data_subtitle")
            })
            appendChild(div {
                style.cssText = "display:flex;gap:8px;justify-content:center;flex-wrap:wrap"
                DataService.sports.filter { it.isDefault }.take(6).forEach { s ->
                    val c = Styles.adaptColor(s.color.ifEmpty { Styles.A1 })
                    appendChild(span("${s.i} ${s.localName()}") {
                        style.cssText = "font-size:12px;font-weight:700;color:$c;background:${c}15;border:1.5px solid ${c}25;border-radius:10px;padding:5px 10px"
                    })
                }
            })
        }
    }
}
